import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { trimEnd } from 'lodash';

import { SECTION } from 'src/constants/section';
import { storedAuthToken } from 'src/services/api/http';
import { routePaths } from 'src/services/routing/routePaths';
import { useUserContext } from 'src/components/app/context/UserContext';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import { useGlobalDashboards } from 'src/components/common/GlobalModules/useGlobalDashboards';
import PageFrame from 'src/components/common/PageFrame';
import PageNotFound from 'src/components/common/PageNotFound';
import LoadingPage from 'src/components/app/LoadingPage';
import useFetchSurveyList from 'src/components/survey/SurveyContext/ListContext/useFetchSurveyList';

export interface RouteGuardProps {
  children?: ReactNode;
  section: number;
}

/**
 * This is a component to handle private routes.
 */
const RouteGuard: FC<RouteGuardProps> = ({ children, section }) => {
  const { user, currentSurvey } = useUserContext();
  const location = useLocation();
  const authToken = storedAuthToken();
  const {
    activeUserPermissionsInCurrentSurvey,
    adminUserPermissions,
    hasMyFollowUpAccess,
  } = useUserPermissions();
  const { data: globalDashboards } = useGlobalDashboards(!!user);
  const {
    data: surveyList,
    isFetching,
    isLoading,
    error: errorSurveyList,
  } = useFetchSurveyList();

  const canSeeSection = useMemo(() => {
    if (user && user.is_superuser) {
      return true;
    }
    // Validate the user has at least one permission module with admin privileges.
    if (section === SECTION.USER_ADMIN) {
      return !!adminUserPermissions.length;
    }

    if (section === SECTION.DASHBOARD) {
      const slug = trimEnd(
        location.pathname.replaceAll('/dashboard/', ''),
        '/',
      );

      return (
        activeUserPermissionsInCurrentSurvey.find(
          (permission) => permission.module.slug === slug,
        ) || globalDashboards?.find((dashboard) => dashboard.slug === slug)
      );
    }

    // The survey settings page requires a specific role
    if (
      section === SECTION.SURVEY &&
      // trim potential trailing slash
      trimEnd(location.pathname, '/') ===
        trimEnd(routePaths.SURVEY_SETTINGS, '/')
    ) {
      return (
        currentSurvey &&
        user &&
        (user.is_superuser || user.role?.canEditSurveySettings)
      );
    }

    // modules that only require users to be authenticated
    if ([SECTION.SURVEY].includes(section)) {
      return !!user;
    }

    // MY_FOLLOW_UP requires FOLLOW_UP_ACTION or FOLLOW_UP module permission in any Survey
    if (section === SECTION.MY_FOLLOW_UP) {
      return hasMyFollowUpAccess();
    }

    // modules that require users to have at least one permission
    if (section === SECTION.FOLLOW_UP_INSPIRATIONS) {
      return activeUserPermissionsInCurrentSurvey.length > 0;
    }

    // Every other module is hard-wired within the SECTION constant.
    return !!activeUserPermissionsInCurrentSurvey.find(
      (permission) => permission.module.module_type === section,
    );
  }, [
    user,
    activeUserPermissionsInCurrentSurvey,
    adminUserPermissions,
    section,
    location.pathname,
    currentSurvey,
    hasMyFollowUpAccess,
    globalDashboards,
  ]);

  // Show the matching route right away if it is inside the public section.
  if (section === SECTION.PUBLIC) {
    return <>{children}</>;
  }

  // Show login page in new tabs.
  if (!authToken) {
    if (location.pathname === routePaths.LOGIN) return null;
    return (
      <Redirect
        to={{
          pathname: routePaths.LOGIN,
          state: { originalLocation: location },
        }}
      />
    );
  }

  // On page reload it takes a moment to load the user context. Rather show nothing
  // than flashing the 404 page until the user context has been loaded.
  if (!user || errorSurveyList) return null;

  // If the user hasn't accepted the terms and conditions redirect the user
  if (!user.pro_terms_accepted)
    return (
      <Redirect
        to={{
          pathname: routePaths.TERMS_OF_USAGE,
          state: { originalLocation: location },
        }}
      />
    );

  // show loading spinner when survey info is not yet available (e.g. for My-Follow-Up checks)
  if (isLoading || (isFetching && !surveyList)) {
    return (
      <PageFrame>
        <LoadingPage />
      </PageFrame>
    );
  }

  if (!canSeeSection)
    return (
      <PageFrame>
        <PageNotFound />
      </PageFrame>
    );

  return <>{children}</>;
};

export default RouteGuard;
